import { AbstractControl, ValidatorFn } from '@angular/forms';
import { isNull, Option } from '@fmnts/core';

export interface MustMatchControlValidationError {
  /** Value of the original control. */
  original: unknown | undefined;
  /** Value of the confirm control. */
  confirm: unknown | undefined;
}

interface ValidatorConfig {
  /** Path to the original control. */
  original: string | (string | number)[];
  /** Path to the confirm control. */
  confirm: string | (string | number)[];
}

const eqeqeq = (a: unknown, b: unknown) => a === b;

const VALIDATION_ERROR_PROP = 'mustMatchControl';

/**
 * Validator to be added on a form group that checks if the value
 * for the control is equal to the value of another control. Uses
 * ` === ` check, so it should be used only on primitive types.
 *
 * ***NOTE:*** Validation errors are added to the control specified by
 * `confirm`.
 * If one of the controls can't be resolved, then an error is added to
 * the group this validator is added to.
 */
export function mustMatchControlValidator({
  original,
  confirm,
}: ValidatorConfig): ValidatorFn {
  return (ctrl) => {
    const originalCtrl: Option<AbstractControl<unknown>> = ctrl.get(original);
    const confirmCtrl: Option<AbstractControl<unknown>> = ctrl.get(confirm);

    if (isNull(confirmCtrl) || isNull(originalCtrl)) {
      return {
        [VALIDATION_ERROR_PROP]: _createValidationError(
          originalCtrl,
          confirmCtrl,
        ),
      };
    }

    const error = _getError(originalCtrl, confirmCtrl, eqeqeq);

    if (error) {
      // Append error
      confirmCtrl?.setErrors({
        ...confirmCtrl?.errors,
        [VALIDATION_ERROR_PROP]: error,
      });
    } else if (confirmCtrl?.hasError(VALIDATION_ERROR_PROP)) {
      // Remove error
      const { [VALIDATION_ERROR_PROP]: _prevError, ...otherErrors } =
        confirmCtrl.errors ?? {};
      const hasOtherErrors = Object.keys(otherErrors).length > 0;

      confirmCtrl.setErrors(hasOtherErrors ? otherErrors : null);
    }

    // Don't add an error on the group itself
    return null;
  };
}

function _getError(
  originalCtrl: AbstractControl<unknown>,
  confirmCtrl: AbstractControl<unknown>,
  compare: (a: unknown, b: unknown) => boolean,
): MustMatchControlValidationError | null {
  // No error when values are considered the same
  if (compare(originalCtrl.value, confirmCtrl.value)) {
    return null;
  }

  return _createValidationError(originalCtrl, confirmCtrl);
}

function _createValidationError(
  originalCtrl: Option<AbstractControl<unknown>>,
  confirmCtrl: Option<AbstractControl<unknown>>,
): MustMatchControlValidationError {
  return {
    original: originalCtrl?.value ?? undefined,
    confirm: confirmCtrl?.value ?? undefined,
  };
}
